import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { env } from '@/lib/env'
import { getMeta, setMeta } from '@/offline/meta'
import { roster, type TeamMember } from '@/offline/roster'
import { ROLES } from '@/auth/roles'
import { verifyPin } from './PinVerifier'
import { NO_ATTEMPTS, isLockedOut, recordFailure, recordSuccess, triesLeft } from './lockout'
import { PhoneSessionContext, type PhoneSessionValue, type UnlockResult } from './phoneSession'

/**
 * Unlock state survives a reload within the idle window (sessionStorage, cleared with the tab) so
 * an app update does not force everyone to re-enter PINs; it never survives closing the app.
 */
const KEY = 'ucsc.phone.actor.v1'
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'touchstart'] as const

/**
 * A supervisor who unlocks someone's phone with his own PIN is not left in charge of it: besides
 * the idle lock, his unlock ends `supervisorUnlockMaxMs` after he entered the PIN, however busy
 * the phone is, so an operator who keeps tapping does not keep acting as him.
 */
function hardStopFor(actor: TeamMember, unlockedAt: number): number | null {
  return actor.roles.includes(ROLES.SUPERVISOR) ? unlockedAt + env.supervisorUnlockMaxMs : null
}

interface Stored {
  actor: TeamMember
  until: number
  hard_stop?: number | null
}

function restore(): Stored | null {
  try {
    const raw = sessionStorage.getItem(KEY)
    if (!raw) return null
    const s = JSON.parse(raw) as Stored
    const now = Date.now()
    if (s.until <= now || (s.hard_stop != null && s.hard_stop <= now)) return null
    return s
  } catch {
    return null
  }
}

function persist(actor: TeamMember | null, hardStop: number | null = null): void {
  try {
    if (actor)
      sessionStorage.setItem(
        KEY,
        JSON.stringify({ actor, until: Date.now() + env.pinIdleLockMs, hard_stop: hardStop }),
      )
    else sessionStorage.removeItem(KEY)
  } catch {
    /* ignore */
  }
}

export function PhoneSessionProvider({ children }: { children: ReactNode }) {
  const [restored] = useState(restore)
  const [actor, setActor] = useState<TeamMember | null>(restored?.actor ?? null)
  const lastActivity = useRef(0)
  const hardStop = useRef<number | null>(restored?.hard_stop ?? null)

  const lock = useCallback(() => {
    hardStop.current = null
    persist(null)
    setActor(null)
  }, [])

  const unlock = useCallback(async (userId: string, pin: string): Promise<UnlockResult> => {
    const now = Date.now()
    const attempts = (await getMeta('pin_attempts')) ?? NO_ATTEMPTS
    if (isLockedOut(attempts, now))
      return { ok: false, reason: 'locked_out', retryInMs: attempts.locked_until - now }

    const member = await roster.teamMember(userId)
    if (!member) return { ok: false, reason: 'unknown_user' }
    if (!member.pin_hash) return { ok: false, reason: 'no_pin' }

    if (await verifyPin(pin, member.pin_hash)) {
      await setMeta('pin_attempts', recordSuccess())
      lastActivity.current = Date.now()
      // Never keep the hash in memory/session storage longer than needed.
      const { pin_hash: _hash, ...safe } = member
      const unlocked = safe as TeamMember
      hardStop.current = hardStopFor(unlocked, lastActivity.current)
      persist(unlocked, hardStop.current)
      setActor(unlocked)
      return { ok: true, actor: unlocked }
    }
    const next = recordFailure(attempts, Date.now())
    await setMeta('pin_attempts', next)
    if (isLockedOut(next, Date.now()))
      return { ok: false, reason: 'locked_out', retryInMs: next.locked_until - Date.now() }
    return { ok: false, reason: 'wrong_pin', triesLeft: triesLeft(next) }
  }, [])

  // Idle lock: no touch for VITE_PIN_IDLE_LOCK_MINUTES → lock (F4.1-t).
  useEffect(() => {
    if (!actor) return
    lastActivity.current = Date.now()
    const touch = () => {
      lastActivity.current = Date.now()
      persist(actor, hardStop.current)
    }
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, touch, { passive: true }))
    const check = () => {
      const now = Date.now()
      if (now - lastActivity.current >= env.pinIdleLockMs) lock()
      else if (hardStop.current !== null && now >= hardStop.current) lock()
    }
    const timer = setInterval(check, 15_000)
    document.addEventListener('visibilitychange', check)
    return () => {
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, touch))
      clearInterval(timer)
      document.removeEventListener('visibilitychange', check)
    }
  }, [actor, lock])

  const value = useMemo<PhoneSessionValue>(() => ({ actor, unlock, lock }), [actor, unlock, lock])
  return <PhoneSessionContext.Provider value={value}>{children}</PhoneSessionContext.Provider>
}
